// Microphone → 16 kHz mono WAV, small enough to send for transcription
// (≈ 32 KB per second). Works in Chrome, Safari, Firefox and the Android
// app's WebView. The sound is resampled while it is recorded, so long
// dictations do not hold minutes of 48 kHz audio in memory.
import { audio } from './audioEngine';

const OUT_RATE = 16000;
const MAX_SECONDS = 10 * 60;

export const canRecord = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === 'function' &&
  !!audio();

function wavBlob(chunks, total) {
  const buf = new ArrayBuffer(44 + total * 2);
  const v = new DataView(buf);
  const w = (o, s) =>
    [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  w(0, 'RIFF');
  v.setUint32(4, 36 + total * 2, true);
  w(8, 'WAVE');
  w(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, OUT_RATE, true);
  v.setUint32(28, OUT_RATE * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  w(36, 'data');
  v.setUint32(40, total * 2, true);
  let o = 44;
  chunks.forEach((c) => {
    for (let i = 0; i < c.length; i++, o += 2) v.setInt16(o, c[i], true);
  });
  return new Blob([buf], { type: 'audio/wav' });
}

/* → { stop(): Promise<{ blob, seconds, voiced }>, cancel(), level() }
   level() is the current loudness 0..1 (for meters and the face);
   voiced = seconds that sounded like speech (silence check). */
export async function startRecording() {
  const a = audio();
  if (!a) throw new Error('Браузер не умеет записывать звук');
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
  const { ctx } = a;
  if (ctx.state === 'suspended') await ctx.resume().catch(() => {});
  const src = ctx.createMediaStreamSource(stream);
  const proc = ctx.createScriptProcessor(2048, 1, 1);
  const mute = ctx.createGain();
  mute.gain.value = 0;

  const ratio = ctx.sampleRate / OUT_RATE;
  const chunks = [];
  let total = 0;
  let acc = 0;
  let accN = 0;
  let next = ratio;
  let pos = 0;
  let level = 0;
  let voiced = 0;
  let noise = 0.01;
  let done = false;

  proc.onaudioprocess = (e) => {
    if (done) return;
    const d = e.inputBuffer.getChannelData(0);
    const out = new Int16Array(Math.ceil(d.length / ratio) + 2);
    let n = 0;
    let sum = 0;
    for (let i = 0; i < d.length; i++) {
      const x = d[i];
      sum += x * x;
      acc += x;
      accN += 1;
      pos += 1;
      if (pos >= next) {
        const s = Math.max(-1, Math.min(1, acc / accN));
        out[n++] = s < 0 ? s * 0x8000 : s * 0x7fff;
        acc = 0;
        accN = 0;
        next += ratio;
      }
    }
    if (total + n > MAX_SECONDS * OUT_RATE) return;
    chunks.push(out.subarray(0, n));
    total += n;
    const rms = Math.sqrt(sum / d.length);
    // adaptive noise floor: speech is clearly above the room's hum
    noise = rms < noise ? noise * 0.9 + rms * 0.1 : noise * 0.999 + rms * 0.001;
    if (rms > Math.max(0.015, noise * 2.5)) voiced += d.length / ctx.sampleRate;
    level = Math.min(1, rms * 6);
  };
  src.connect(proc);
  proc.connect(mute);
  mute.connect(ctx.destination);

  const close = () => {
    done = true;
    try {
      src.disconnect();
      proc.disconnect();
      mute.disconnect();
    } catch (e) {
      // already disconnected
    }
    stream.getTracks().forEach((t) => t.stop());
  };

  return {
    level: () => (done ? 0 : level),
    cancel: close,
    async stop() {
      // let the last buffer arrive
      await new Promise((r) => setTimeout(r, 120));
      close();
      return {
        blob: wavBlob(chunks, total),
        seconds: total / OUT_RATE,
        voiced,
      };
    },
  };
}

/* Send a recording to the server → the text ('' when nothing was said). */
export async function transcribe(blob, { signal } = {}) {
  const res = await fetch('/api/voice/listen', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'audio/wav' },
    body: blob,
    signal,
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.message || `HTTP ${res.status}`);
  return (j.text || '').trim();
}
